import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SeoHead from "@/components/feature/SeoHead";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://www.crownhongyunmall.com";

const resellerJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "name": "分销商仪表盘 | 皇冠鸿运商城",
  "description": "皇冠鸿运商城分销商专属后台，查看佣金收益、推荐记录、提现记录，获取推广工具，邀请好友赚取佣金。",
  "url": `${SITE_URL}/reseller-dashboard`,
  "noIndex": true
};

const resellerData = {
  name: "张伟",
  level: "黄金分销商",
  levelIcon: "ri-medal-line",
  levelColor: "from-yellow-400 to-amber-500",
  joinDate: "2025-10-01",
  referralCode: "CROWN-ZW8821",
  referralLink: "https://crown-hongyun.com/?ref=ZW8821",
  totalEarnings: 28640,
  monthEarnings: 4320,
  pendingPayout: 1850,
  totalReferrals: 312,
  activeReferrals: 187,
  conversionRate: 60,
  commissionRate: "12%",
  nextLevelName: "钻石分销商",
  nextLevelTarget: 500,
  currentProgress: 312,
};

const earningsChart = [
  { month: "10月", amount: 1200 },
  { month: "11月", amount: 2100 },
  { month: "12月", amount: 3800 },
  { month: "1月", amount: 2900 },
  { month: "2月", amount: 3200 },
  { month: "3月", amount: 4320 },
];

const referrals = [
  { id: 1, name: "李**", date: "2026-03-28", status: "已购买", order: "iPhone 15 Pro", commission: 120, avatar: "L", color: "bg-orange-500" },
  { id: 2, name: "王**", date: "2026-03-25", status: "已购买", order: "戴森吸尘器", commission: 54, avatar: "W", color: "bg-red-500" },
  { id: 3, name: "陈**", date: "2026-03-22", status: "已购买", order: "SK-II 礼盒", commission: 19, avatar: "C", color: "bg-green-500" },
  { id: 4, name: "赵**", date: "2026-03-20", status: "浏览中", order: "—", commission: 0, avatar: "Z", color: "bg-teal-500" },
  { id: 5, name: "刘**", date: "2026-03-18", status: "已购买", order: "华为 Mate 60", commission: 84, avatar: "L", color: "bg-pink-500" },
  { id: 6, name: "孙**", date: "2026-03-15", status: "已购买", order: "耐克 Air Jordan", commission: 16, avatar: "S", color: "bg-amber-500" },
  { id: 7, name: "周**", date: "2026-03-12", status: "已注册", order: "—", commission: 0, avatar: "Z", color: "bg-indigo-500" },
  { id: 8, name: "吴**", date: "2026-03-10", status: "已购买", order: "小米 14 Ultra", commission: 72, avatar: "W", color: "bg-cyan-500" },
];

const payouts = [
  { id: "PAY-20260301", date: "2026-03-01", amount: 3200, status: "已到账", method: "支付宝", note: "2月佣金结算" },
  { id: "PAY-20260201", date: "2026-02-01", amount: 2900, status: "已到账", method: "微信支付", note: "1月佣金结算" },
  { id: "PAY-20260101", date: "2026-01-01", amount: 3800, status: "已到账", method: "支付宝", note: "12月佣金结算" },
  { id: "PAY-20251201", date: "2025-12-01", amount: 2100, status: "已到账", method: "银行转账", note: "11月佣金结算" },
  { id: "PAY-20251101", date: "2025-11-01", amount: 1200, status: "已到账", method: "支付宝", note: "10月佣金结算" },
];

const maxAmount = Math.max(...earningsChart.map((d) => d.amount));

type TabType = "overview" | "referrals" | "payouts" | "tools" | "invite";

export default function ResellerDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [copied, setCopied] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [posterStyle, setPosterStyle] = useState<"red" | "dark" | "gold">("red");
  const [shareSuccess, setShareSuccess] = useState(false);

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleWithdraw = () => {
    setWithdrawSuccess(true);
    setTimeout(() => {
      setWithdrawSuccess(false);
      setWithdrawOpen(false);
    }, 2000);
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(resellerData.referralCode).catch(() => {});
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleShare = () => {
    setShareSuccess(true);
    setTimeout(() => setShareSuccess(false), 2000);
  };

  const progressPct = Math.min(100, (resellerData.currentProgress / resellerData.nextLevelTarget) * 100);

  return (
    <div className="min-h-screen bg-gray-50">
      <SeoHead
        title="分销商仪表盘 | 佣金收益 | 皇冠鸿运商城"
        description="皇冠鸿运商城分销商专属后台，查看佣金收益、推荐记录、提现记录，获取推广工具，邀请好友赚取佣金。"
        keywords="分销商,佣金收益,推广工具,皇冠鸿运商城"
        canonical="/reseller-dashboard"
        jsonLd={resellerJsonLd}
      />
      {/* Top bar */}
      <div className="bg-red-600 text-white text-xs py-1.5 px-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <span>皇冠鸿运商城 — 分销商专属后台</span>
          <a href="/" className="hover:underline cursor-pointer flex items-center gap-1">
            <i className="ri-home-line"></i> 返回首页
          </a>
        </div>
      </div>

      {/* Nav */}
      <nav className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/" className="flex items-center gap-2 cursor-pointer">
              <img
                src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
                alt="Logo"
                className="h-9 w-auto object-contain"
              />
              <span className="text-red-600 font-bold text-lg hidden sm:block whitespace-nowrap" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                皇冠鸿运商城
              </span>
            </a>
            <span className="text-gray-300 hidden sm:block">|</span>
            <span className="text-gray-600 text-sm font-medium hidden sm:block">分销商仪表盘</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 flex items-center justify-center bg-gradient-to-br ${resellerData.levelColor} rounded-full`}>
                <i className={`${resellerData.levelIcon} text-white text-sm`}></i>
              </div>
              <div className="hidden sm:block">
                <p className="text-gray-800 font-semibold text-sm leading-none">{resellerData.name}</p>
                <p className="text-gray-400 text-xs mt-0.5">{resellerData.level}</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/")}
              className="text-gray-400 hover:text-red-500 cursor-pointer text-sm whitespace-nowrap"
            >
              <i className="ri-logout-box-line"></i>
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header card */}
        <div className={`bg-gradient-to-r ${resellerData.levelColor} rounded-2xl p-6 mb-6 text-white`}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 flex items-center justify-center bg-white/20 rounded-2xl">
                <i className={`${resellerData.levelIcon} text-white text-2xl`}></i>
              </div>
              <div>
                <p className="text-white/70 text-sm">欢迎回来</p>
                <h1 className="text-2xl font-bold" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                  {resellerData.name}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="bg-white/20 text-white text-xs rounded-full font-medium">
                    {resellerData.level}
                  </span>
                  <span className="text-white/60 text-xs">加入于 {resellerData.joinDate}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-start sm:items-end gap-2">
              <div className="text-right">
                <p className="text-white/70 text-xs">本月佣金</p>
                <p className="text-3xl font-bold">¥{resellerData.monthEarnings.toLocaleString()}</p>
              </div>
              <button
                onClick={() => setWithdrawOpen(true)}
                className="bg-white text-amber-600 px-5 py-2 rounded-xl font-bold text-sm transition-all hover:bg-white/90 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-bank-card-line mr-1"></i>申请提现 ¥{resellerData.pendingPayout.toLocaleString()}
              </button>
            </div>
          </div>

          {/* Level progress */}
          <div className="mt-5 bg-white/10 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-white/80 text-xs">升级进度 → {resellerData.nextLevelName}</span>
              <span className="text-white text-xs font-medium">{resellerData.currentProgress} / {resellerData.nextLevelTarget} 人</span>
            </div>
            <div className="bg-white/20 rounded-full h-2">
              <div
                className="bg-white rounded-full h-2 transition-all duration-500"
                style={{ width: `${progressPct}%` }}
              ></div>
            </div>
            <p className="text-white/60 text-xs mt-1.5">
              还需推荐 {resellerData.nextLevelTarget - resellerData.currentProgress} 人即可升级
            </p>
          </div>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "累计总收益", value: `¥${resellerData.totalEarnings.toLocaleString()}`, icon: "ri-money-cny-circle-line", color: "text-red-500", bg: "bg-red-50" },
            { label: "推荐总人数", value: `${resellerData.totalReferrals}人`, icon: "ri-user-add-line", color: "text-green-500", bg: "bg-green-50" },
            { label: "活跃用户", value: `${resellerData.activeReferrals}人`, icon: "ri-user-heart-line", color: "text-orange-500", bg: "bg-orange-50" },
            { label: "转化率", value: `${resellerData.conversionRate}%`, icon: "ri-line-chart-line", color: "text-teal-500", bg: "bg-teal-50" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-gray-400 text-xs">{stat.label}</p>
                <div className={`w-8 h-8 flex items-center justify-center ${stat.bg} rounded-lg`}>
                  <i className={`${stat.icon} ${stat.color} text-base`}></i>
                </div>
              </div>
              <p className="text-gray-900 font-bold text-2xl">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-100 overflow-x-auto">
            {([
              { id: "overview", label: "收益概览", icon: "ri-bar-chart-line" },
              { id: "referrals", label: "推荐记录", icon: "ri-user-add-line" },
              { id: "payouts", label: "提现记录", icon: "ri-bank-card-line" },
              { id: "tools", label: "推广工具", icon: "ri-tools-line" },
              { id: "invite", label: "邀请好友", icon: "ri-user-shared-line" },
            ] as { id: TabType; label: string; icon: string }[]).map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "text-red-500 border-b-2 border-red-500"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={tab.icon}></i>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {activeTab === "overview" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-gray-800 font-bold text-lg">近6个月收益趋势</h2>
                  <span className="text-gray-400 text-xs">佣金比例：{resellerData.commissionRate}</span>
                </div>
                {/* Bar chart */}
                <div className="flex items-end gap-3 h-48 mb-2">
                  {earningsChart.map((d) => {
                    const pct = (d.amount / maxAmount) * 100;
                    const isLast = d.month === "3月";
                    return (
                      <div key={d.month} className="flex-1 flex flex-col items-center gap-2">
                        <span className="text-gray-600 text-xs font-medium">¥{(d.amount / 1000).toFixed(1)}k</span>
                        <div className="w-full relative" style={{ height: `${pct}%`, minHeight: "20px" }}>
                          <div
                            className={`w-full h-full rounded-t-lg ${isLast ? "bg-red-500" : "bg-red-200"} transition-all duration-500`}
                          ></div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-3">
                  {earningsChart.map((d) => (
                    <div key={d.month} className="flex-1 text-center text-xs text-gray-400">{d.month}</div>
                  ))}
                </div>

                {/* Commission breakdown */}
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {[
                    { label: "手机数码", amount: 1840, pct: 43, color: "bg-red-500" },
                    { label: "美妆护肤", amount: 1120, pct: 26, color: "bg-pink-500" },
                    { label: "家用电器", amount: 960, pct: 22, color: "bg-orange-500" },
                  ].map((cat) => (
                    <div key={cat.label} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-600 text-sm font-medium">{cat.label}</span>
                        <span className="text-gray-400 text-xs">{cat.pct}%</span>
                      </div>
                      <p className="text-gray-900 font-bold text-xl mb-2">¥{cat.amount.toLocaleString()}</p>
                      <div className="bg-gray-200 rounded-full h-1.5">
                        <div className={`${cat.color} h-1.5 rounded-full`} style={{ width: `${cat.pct}%` }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Referrals Tab */}
            {activeTab === "referrals" && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">推荐记录</h2>
                  <span className="text-gray-400 text-xs">共 {resellerData.totalReferrals} 条记录</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        {["用户", "推荐日期", "状态", "购买商品", "佣金"].map((h) => (
                          <th key={h} className="text-left text-gray-400 text-xs font-medium pb-3 pr-4 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.map((r) => (
                        <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                          <td className="py-3 pr-4">
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 flex items-center justify-center ${r.color} rounded-full text-white font-bold text-xs flex-shrink-0`}>
                                {r.avatar}
                              </div>
                              <span className="text-gray-700 font-medium whitespace-nowrap">{r.name}</span>
                            </div>
                          </td>
                          <td className="py-3 pr-4 text-gray-400 text-xs whitespace-nowrap">{r.date}</td>
                          <td className="py-3 pr-4">
                            <span className={`text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap ${
                              r.status === "已购买" ? "bg-green-100 text-green-600" :
                              r.status === "已注册" ? "bg-orange-100 text-orange-600" :
                              "bg-gray-100 text-gray-500"
                            }`}>
                              {r.status}
                            </span>
                          </td>
                          <td className="py-3 pr-4 text-gray-600 text-xs whitespace-nowrap">{r.order}</td>
                          <td className="py-3">
                            <span className={`font-bold text-sm ${r.commission > 0 ? "text-red-500" : "text-gray-300"}`}>
                              {r.commission > 0 ? `+¥${r.commission}` : "—"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Payouts Tab */}
            {activeTab === "payouts" && (
              <div>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-gray-800 font-bold text-lg">提现记录</h2>
                  <button
                    onClick={() => setWithdrawOpen(true)}
                    className="bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-xl text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-bank-card-line mr-1"></i>申请提现
                  </button>
                </div>

                {/* Pending payout card */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl">
                      <i className="ri-time-line text-amber-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-gray-800 font-semibold text-sm">待提现余额</p>
                      <p className="text-gray-400 text-xs">每月1日自动结算，可随时申请提现</p>
                    </div>
                  </div>
                  <p className="text-amber-600 font-bold text-2xl">¥{resellerData.pendingPayout.toLocaleString()}</p>
                </div>

                <div className="space-y-3">
                  {payouts.map((p) => (
                    <div key={p.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                          <i className="ri-check-line text-green-500 text-lg"></i>
                        </div>
                        <div>
                          <p className="text-gray-800 font-medium text-sm">{p.note}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-gray-400 text-xs">{p.date}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-gray-400 text-xs">{p.method}</span>
                            <span className="text-gray-300">·</span>
                            <span className="text-gray-400 text-xs">{p.id}</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-green-500 font-bold text-lg">+¥{p.amount.toLocaleString()}</p>
                        <span className="text-green-500 text-xs bg-green-100 px-2 py-0.5 rounded-full">{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Invite Tab */}
            {activeTab === "invite" && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-gray-800 font-bold text-lg">邀请好友，赚取佣金</h2>
                    <p className="text-gray-400 text-sm mt-1">分享专属邀请海报，好友注册购买即可获得佣金</p>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                  {/* Left: poster preview */}
                  <div className="flex-1">
                    <p className="text-gray-600 font-semibold text-sm mb-3">选择海报风格</p>
                    <div className="flex gap-3 mb-5">
                      {([
                        { id: "red", label: "红色喜庆", from: "from-red-500", to: "to-orange-500" },
                        { id: "dark", label: "深色高端", from: "from-gray-800", to: "to-gray-900" },
                        { id: "gold", label: "金色尊贵", from: "from-yellow-500", to: "to-amber-600" },
                      ] as { id: "red" | "dark" | "gold"; label: string; from: string; to: string }[]).map((s) => (
                        <button
                          key={s.id}
                          onClick={() => setPosterStyle(s.id)}
                          className={`flex-1 py-2 rounded-xl text-xs font-medium transition-all cursor-pointer whitespace-nowrap border-2 ${
                            posterStyle === s.id ? "border-red-500 bg-red-50 text-red-500" : "border-gray-200 text-gray-500 hover:border-gray-300"
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>

                    {/* Poster preview */}
                    <div className={`relative rounded-2xl overflow-hidden ${
                      posterStyle === "red" ? "bg-gradient-to-br from-red-500 to-orange-500" :
                      posterStyle === "dark" ? "bg-gradient-to-br from-gray-800 to-gray-900" :
                      "bg-gradient-to-br from-yellow-500 to-amber-600"
                    }`} style={{ aspectRatio: "9/16", maxWidth: "280px", margin: "0 auto" }}>
                      {/* Poster background image */}
                      <img
                        src={
                          posterStyle === "red"
                            ? "https://public.readdy.ai/ai/img_res/88f5dc4931d1bd2b1a38654a128c1e18.jpg"
                            : posterStyle === "dark"
                            ? "https://readdy.ai/api/search-image?query=dark%20luxury%20premium%20background%20with%20subtle%20golden%20geometric%20patterns%2C%20elegant%20abstract%20texture%2C%20no%20text%2C%20vertical%20portrait%20format&width=280&height=498&seq=poster_dark&orientation=portrait"
                            : "https://readdy.ai/api/search-image?query=golden%20luxury%20premium%20background%20with%20shimmering%20light%20effects%2C%20elegant%20abstract%20texture%2C%20warm%20gold%20tones%2C%20no%20text%2C%20vertical%20portrait%20format&width=280&height=498&seq=poster_gold&orientation=portrait"
                        }
                        alt="海报背景"
                        className="absolute inset-0 w-full h-full object-cover opacity-30"
                      />
                      <div className="relative p-6 flex flex-col h-full">
                        {/* Logo area */}
                        <div className="flex items-center gap-2 mb-4">
                          <img
                            src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
                            alt="Logo"
                            className="w-10 h-10 object-contain"
                          />
                          <div>
                            <p className="text-white font-bold text-sm" style={{ fontFamily: "'Noto Serif SC', serif" }}>皇冠鸿运商城</p>
                            <p className="text-white/60 text-xs">Crown HongYun Mall</p>
                          </div>
                        </div>

                        {/* Main message */}
                        <div className="flex-1 flex flex-col justify-center">
                          <p className="text-white/80 text-xs mb-2">我的好友 {resellerData.name} 邀请你</p>
                          <h3 className="text-white font-bold text-2xl leading-tight mb-3" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                            加入皇冠鸿运<br />享受购物优惠
                          </h3>
                          <div className="space-y-2 mb-4">
                            {["新人专享 9 折优惠", "首单免运费", "正品保障 假一赔十"].map((item) => (
                              <div key={item} className="flex items-center gap-2">
                                <div className="w-4 h-4 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                                  <i className="ri-check-line text-white text-xs"></i>
                                </div>
                                <span className="text-white/90 text-xs">{item}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* QR code area */}
                        <div className="bg-white rounded-xl p-3 flex items-center gap-3">
                          {/* Simulated QR code */}
                          <div className="w-16 h-16 flex-shrink-0 bg-gray-900 rounded-lg p-1.5">
                            <div className="grid grid-cols-7 gap-px h-full">
                              {Array.from({ length: 49 }).map((_, i) => {
                                const qrPattern = [0,1,2,3,4,5,6,7,13,14,20,21,27,28,34,35,41,42,43,44,45,46,47,48,8,15,22,29,36,10,12,17,19,24,26,31,33,38,40,9,11,16,18,23,25,30,32,37,39];
                                return (
                                  <div
                                    key={i}
                                    className={`rounded-sm ${qrPattern.includes(i) ? "bg-white" : "bg-gray-900"}`}
                                  ></div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-gray-800 font-bold text-xs">扫码立即注册</p>
                            <p className="text-gray-400 text-xs mt-0.5">推荐码</p>
                            <p className="text-red-500 font-bold text-sm">{resellerData.referralCode}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right: share options */}
                  <div className="flex-1">
                    <p className="text-gray-600 font-semibold text-sm mb-4">分享方式</p>

                    {/* Referral code */}
                    <div className="bg-gray-50 rounded-xl p-4 mb-4">
                      <p className="text-gray-500 text-xs mb-2">我的专属推荐码</p>
                      <div className="flex items-center gap-3">
                        <div className="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-3 text-center">
                          <p className="text-red-500 font-bold text-xl tracking-widest">{resellerData.referralCode}</p>
                        </div>
                        <button
                          onClick={handleCopyCode}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                            copiedCode ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
                          }`}
                        >
                          {copiedCode ? <><i className="ri-check-line mr-1"></i>已复制</> : <><i className="ri-file-copy-line mr-1"></i>复制</>}
                        </button>
                      </div>
                    </div>

                    {/* Share channels */}
                    <div className="grid grid-cols-2 gap-3 mb-5">
                      {[
                        { icon: "ri-wechat-line", label: "微信好友", color: "bg-green-500", desc: "发送给微信好友" },
                        { icon: "ri-wechat-2-line", label: "朋友圈", color: "bg-green-600", desc: "分享到朋友圈" },
                        { icon: "ri-weibo-line", label: "微博", color: "bg-red-500", desc: "发布到微博" },
                        { icon: "ri-link", label: "复制链接", color: "bg-gray-600", desc: "复制推荐链接" },
                      ].map((ch) => (
                        <button
                          key={ch.label}
                          onClick={handleShare}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer text-left"
                        >
                          <div className={`w-10 h-10 flex items-center justify-center ${ch.color} rounded-xl flex-shrink-0`}>
                            <i className={`${ch.icon} text-white text-lg`}></i>
                          </div>
                          <div>
                            <p className="text-gray-800 font-semibold text-sm">{ch.label}</p>
                            <p className="text-gray-400 text-xs">{ch.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Download poster */}
                    <button
                      onClick={handleShare}
                      className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap mb-3 ${
                        shareSuccess ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
                      }`}
                    >
                      {shareSuccess ? (
                        <><i className="ri-check-line mr-2"></i>海报已保存到相册！</>
                      ) : (
                        <><i className="ri-download-line mr-2"></i>下载邀请海报</>
                      )}
                    </button>

                    {/* Invite stats */}
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-xl p-4">
                      <p className="text-gray-700 font-semibold text-sm mb-3">
                        <i className="ri-trophy-line mr-1 text-amber-500"></i>邀请奖励说明
                      </p>
                      <div className="space-y-2">
                        {[
                          { action: "好友注册", reward: "获得 ¥5 奖励" },
                          { action: "好友首次购买", reward: "获得订单金额 12% 佣金" },
                          { action: "好友每次购买", reward: "持续获得 12% 佣金" },
                          { action: "邀请满 500 人", reward: "升级钻石分销商，佣金提升至 15%" },
                        ].map((item) => (
                          <div key={item.action} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="w-1.5 h-1.5 bg-red-400 rounded-full flex-shrink-0"></div>
                              <span className="text-gray-500 text-xs">{item.action}</span>
                            </div>
                            <span className="text-red-500 font-semibold text-xs">{item.reward}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tools Tab */}
            {activeTab === "tools" && (
              <div>
                <h2 className="text-gray-800 font-bold text-lg mb-5">推广工具</h2>

                {/* Referral link */}
                <div className="bg-gray-50 rounded-xl p-5 mb-5">
                  <p className="text-gray-700 font-semibold text-sm mb-3">
                    <i className="ri-links-line mr-1 text-red-500"></i>我的专属推荐链接
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-white border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-600 font-mono overflow-hidden text-ellipsis whitespace-nowrap">
                      {resellerData.referralLink}
                    </div>
                    <button
                      onClick={() => handleCopy(resellerData.referralLink)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                        copied ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
                      }`}
                    >
                      {copied ? <><i className="ri-check-line mr-1"></i>已复制</> : <><i className="ri-file-copy-line mr-1"></i>复制</>}
                    </button>
                  </div>
                  <p className="text-gray-400 text-xs mt-2">
                    推荐码：<strong className="text-gray-600">{resellerData.referralCode}</strong>
                  </p>
                </div>

                {/* Promo materials */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                  {[
                    { title: "朋友圈海报", desc: "精美竖版海报，适合微信朋友圈分享", icon: "ri-image-line", color: "bg-pink-50 text-pink-500" },
                    { title: "商品推广图", desc: "热销商品推广图，含专属推荐码", icon: "ri-shopping-bag-line", color: "bg-orange-50 text-orange-500" },
                    { title: "短视频素材", desc: "15秒短视频模板，适合抖音快手", icon: "ri-video-line", color: "bg-red-50 text-red-500" },
                    { title: "文字话术", desc: "经过验证的高转化推广文案模板", icon: "ri-file-text-line", color: "bg-teal-50 text-teal-500" },
                  ].map((tool) => (
                    <div key={tool.title} className="bg-gray-50 rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-gray-100 transition-colors">
                      <div className={`w-12 h-12 flex items-center justify-center ${tool.color} rounded-xl flex-shrink-0`}>
                        <i className={`${tool.icon} text-xl`}></i>
                      </div>
                      <div className="flex-1">
                        <p className="text-gray-800 font-semibold text-sm">{tool.title}</p>
                        <p className="text-gray-400 text-xs mt-0.5">{tool.desc}</p>
                      </div>
                      <button className="text-gray-400 hover:text-red-500 cursor-pointer transition-colors">
                        <i className="ri-download-line text-lg"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Commission rules */}
                <div className="bg-red-50 border border-red-100 rounded-xl p-5">
                  <h3 className="text-gray-800 font-bold text-sm mb-4">
                    <i className="ri-information-line mr-1 text-red-500"></i>佣金规则说明
                  </h3>
                  <div className="space-y-2">
                    {[
                      { level: "白银分销商", commission: "10%", req: "推荐 0–199 人" },
                      { level: "黄金分销商", commission: "12%", req: "推荐 200–499 人" },
                      { level: "钻石分销商", commission: "15%", req: "推荐 500+ 人" },
                    ].map((rule) => (
                      <div key={rule.level} className="flex items-center justify-between py-2 border-b border-red-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <i className="ri-medal-line text-amber-500 text-sm"></i>
                          <span className="text-gray-700 text-sm">{rule.level}</span>
                          <span className="text-gray-400 text-xs">({rule.req})</span>
                        </div>
                        <span className="text-red-500 font-bold text-sm">{rule.commission} 佣金</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-gray-400 text-xs mt-3">
                    <i className="ri-gift-line mr-1 text-red-400"></i>
                    分销商会员永久免费，无需缴纳任何费用
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Withdraw Modal */}
      {withdrawOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setWithdrawOpen(false)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-gray-800 font-bold text-lg">申请提现</h3>
              <button onClick={() => setWithdrawOpen(false)} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 mb-5 text-center">
              <p className="text-gray-500 text-xs mb-1">可提现金额</p>
              <p className="text-amber-600 font-bold text-3xl">¥{resellerData.pendingPayout.toLocaleString()}</p>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">提现方式</label>
                <select className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none cursor-pointer">
                  <option>支付宝</option>
                  <option>微信支付</option>
                  <option>银行转账</option>
                </select>
              </div>
              <div>
                <label className="text-gray-600 text-xs font-medium block mb-1.5">提现金额</label>
                <input
                  type="number"
                  defaultValue={resellerData.pendingPayout}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-sm text-gray-700 outline-none"
                />
              </div>
            </div>
            <button
              onClick={handleWithdraw}
              className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                withdrawSuccess ? "bg-green-500 text-white" : "bg-red-500 hover:bg-red-600 text-white"
              }`}
            >
              {withdrawSuccess ? (
                <><i className="ri-check-line mr-1"></i>提现申请已提交！</>
              ) : (
                <><i className="ri-bank-card-line mr-1"></i>确认提现</>
              )}
            </button>
            <p className="text-gray-400 text-xs text-center mt-3">预计 1–3 个工作日到账</p>
          </div>
        </div>
      )}
    </div>
  );
}
